import React, {useEffect} from 'react';
import { useHistory, useParams } from 'react-router-dom';
import NumberFormat from 'react-number-format';
import { useDispatch, useSelector } from 'react-redux';
import { getCustomerSubscriptionShippingApi, editCustomerSubscriptionShippingApi, getSubscriptionStorePickupAddressApi, getStoreConfigsApi } from 'api';
import { countriesCodes, states } from 'helpers/usStates';
import {
  Button,
  Grid,
  Icon,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@material-ui/core';
import { Formik } from 'formik';
import * as yup from 'yup';
import { addCountryCode } from 'helpers/other';


const ShippingForm = ({goBackPath}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {id} = useParams<{ id: string }>();
  const { storeConfigs } = useSelector((state: IReduxState) => state.store_configs);

  const { customerSubscriptionShipping, subscriptionStorePickupAddresses } = useSelector((state: IReduxState) => state.customer_subscriptions);
  const postcodeRegRxp = /(^\d{5}$)|(^\d{5}-\d{4}$)/;

  useEffect(() => {
    if (storeConfigs.length === 0) {
      getStoreConfigsApi({dispatch});
    }

    getCustomerSubscriptionShippingApi({dispatch, id});
    getSubscriptionStorePickupAddressApi({dispatch});
  }, []);

  const defaultShippingMethod = [{
    'code': storeConfigs?.find((config) => config.path === 'carriers/flatrate/active')?.value == 1 ? 'flatrate_flatrate' : '',
    'carrier_title': storeConfigs?.find((config) => config.path === 'carriers/flatrate/title')?.value || '',
    'method_title': 'Flat Rate',
    'price': storeConfigs?.find((config) => config.path === 'carriers/flatrate/price')?.value  || '',
  }];

  const handleSaveShipping = (values) => {
    let selectedAddress = {
      method: '',
      address: {}
    };

    if (values.shipping_address) {
      selectedAddress = {
        method: values.subscription_shipping_method,
        address: {
          ...customerSubscriptionShipping.addresses.find((adr) => adr.id === values.shipping_address),
          default_shipping: true,
          default_billing: true
        }
      };
    } else {
      const selectedRegion = states.find((el) => el.value === values.state.toString());

      selectedAddress = {
        method: values.subscription_shipping_method,
        address: {
          region: {
            region_code: selectedRegion.shortName,
            region: selectedRegion.name,
            region_id: selectedRegion.value
          },
          region_id: selectedRegion.value,
          country_id: values.country_id,
          street: [
            values.street
          ],
          company: values.company,
          telephone: values.telephone,
          postcode: values.postcode,
          city: values.city,
          firstname: values.firstname,
          lastname: values.lastname,
          default_shipping: true,
          default_billing: true
        }
      };
    }

    editCustomerSubscriptionShippingApi({dispatch, id, shipping: selectedAddress});
  };

  const customerPhoneNumber = addCountryCode(customerSubscriptionShipping?.subscription_address?.telephone);
  const isMDIIntegrationEnabled = storeConfigs.find((config) => config.path === 'subscriptions/general/enable_first_free_shipping_total')?.value == 1;

  return (
    <>
      <Formik
        initialValues={{
          shipping_methods: isMDIIntegrationEnabled ? defaultShippingMethod : customerSubscriptionShipping?.shipping_methods || [],
          subscription_shipping_method: customerSubscriptionShipping?.subscription_shipping_method || '',
          shipping_address: 0,
          firstname: customerSubscriptionShipping?.subscription_address?.firstname || '',
          lastname: customerSubscriptionShipping?.subscription_address?.lastname,
          company: customerSubscriptionShipping?.subscription_address?.company || '',
          telephone: customerPhoneNumber,
          street: customerSubscriptionShipping?.subscription_address?.street.join(' '),
          city: customerSubscriptionShipping?.subscription_address?.city,
          postcode: customerSubscriptionShipping?.subscription_address?.postcode,
          state: customerSubscriptionShipping?.subscription_address?.region_id,
          country_id: customerSubscriptionShipping?.subscription_address?.country_id
        }}
        validationSchema={yup.object({
          postcode: yup.string()
            .when('shipping_address', {
              is: 0,
              then: yup
                .string()
                .matches(
                  postcodeRegRxp,
                  'Please enter a valid zip code (Ex: 90602 or 90602-1234)')
                .required('Field is required')
            }),
          city: yup.string()
            .when('shipping_address', {
              is: 0,
              then: yup.string().required('Field is required'),
            }),
          firstname: yup.string()
            .when('shipping_address', {
              is: 0,
              then: yup.string().required('Field is required'),
            }),
          lastname: yup.string()
            .when('shipping_address', {
              is: 0,
              then: yup.string().required('Field is required'),
            }),
          telephone: yup.string()
            .when(['shipping_address', 'subscription_shipping_method'], {
              is: (shipping_address, subscription_shipping_method) =>
                shipping_address === 0 && !subscription_shipping_method.includes('pickupatstore'),
              then: yup
                .string()
                .min(11, 'Enter valid phone number')
                .max(11, 'Enter valid phone number')
                .required('Field is required'),
              otherwise: yup.string().nullable(),
            }),
          street: yup.string()
            .when('shipping_address', {
              is: 0,
              then: yup.string().required('Field is required'),
            }),
          state: yup.string()
            .when('shipping_address', {
              is: 0,
              then: yup.string().required('Field is required'),
            }),
          country_id: yup.string()
            .when('shipping_address', {
              is: 0,
              then: yup.string().required('Field is required'),
            }),
        })}
        enableReinitialize
        onSubmit={(values) => handleSaveShipping(values)}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue
        }) => (
          <form noValidate autoComplete="off" className="py-4 px-2" onSubmit={handleSubmit}>
            <Grid container spacing={2} className="flex items-center">
              <Grid item xs={12} sm={6}>
                <Grid container>
                  <Grid xs={12}>
                    <Typography className="mb-2" variant="h6">
                      Shipping Method
                    </Typography>
                    <Select
                      fullWidth
                      variant="outlined"
                      name="shipping_methods"
                      value={values.subscription_shipping_method}
                      onChange={(event) => {
                        setFieldValue('subscription_shipping_method', event.target.value);
                      }}
                      onBlur={handleBlur}
                    >
                      {values.shipping_methods?.map((el) => (
                        <MenuItem key={el.code} value={el.code}>
                          {`${el.carrier_title} - ${el.method_title}`}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography className="mt-2 text-13 text-light-gray">
                      Rate costs are estimated, and may vary overtime.
                    </Typography>
                  </Grid>
                  {!values.subscription_shipping_method.includes('pickupatstore') ?
                    <>
                      <Grid xs={12}>
                        <Typography className="mb-2 mt-4" variant="h6">
                          Shipping Address
                        </Typography>
                        <Select
                          fullWidth
                          variant="outlined"
                          name="shipping_address"
                          value={values.shipping_address}
                          onChange={(event) => {
                            setFieldValue('shipping_address', event.target.value);
                          }}
                        >
                          <MenuItem value={0}>
                            Enter below
                          </MenuItem>
                          {customerSubscriptionShipping?.addresses?.map((el) => (
                            <MenuItem key={el.id} value={el.id}>
                              {`${el.firstname} ${el.lastname}, ${el.street.join(' ')}, ${el.city}, ${el.region.region}`}
                            </MenuItem>
                          ))}
                        </Select>
                        <Typography className="mt-2 text-13 text-light-gray">
                          Available shipping methods may change after saving an address change.
                        </Typography>
                      </Grid>
                      {values.shipping_address === 0 ?
                        <>
                          <Grid xs={12} sm={6}>
                            <Typography className="mb-2 mt-4" variant="h6">
                              First Name <span className="text-error">*</span>
                            </Typography>
                            <TextField
                              fullWidth
                              variant="outlined"
                              name="firstname"
                              value={values.firstname}
                              onChange={handleChange}
                              onBlur={(event) => {
                                handleBlur(event);
                              }}
                              error={touched?.firstname && Boolean(errors?.firstname)}
                              helperText={touched?.firstname && errors?.firstname}
                            />
                          </Grid>
                          <Grid xs={12} sm={6}>
                            <div className="ml-2">
                              <Typography className="mb-2 mt-4" variant="h6">
                                Last Name <span className="text-error">*</span>
                              </Typography>
                              <TextField
                                fullWidth
                                variant="outlined"
                                name="lastname"
                                value={values.lastname}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched?.lastname && Boolean(errors?.lastname)}
                                helperText={touched?.lastname && errors?.lastname}
                              />
                            </div>
                          </Grid>
                          <Grid xs={12} sm={6}>
                            <Typography className="mb-2 mt-4" variant="h6">
                              Company
                            </Typography>
                            <TextField
                              fullWidth
                              variant="outlined"
                              name="company"
                              value={values.company}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched?.company && Boolean(errors?.company)}
                              helperText={touched?.company && errors?.company}
                            />
                          </Grid>
                          <Grid xs={12} sm={6}>
                            <div className="ml-2">
                              <Typography className="mb-2 mt-4" variant="h6">
                                Phone <span className="text-error">*</span>
                              </Typography>
                              <NumberFormat
                                customInput={TextField}
                                format="+# (###) ###-####"
                                fullWidth
                                required
                                variant="outlined"
                                name="telephone"
                                value={values.telephone || null}
                                onValueChange={(value) => {
                                  setFieldValue('telephone', String(value.floatValue));
                                }}
                                onBlur={handleBlur}
                                error={touched?.telephone && Boolean(errors?.telephone)}
                                helperText={touched?.telephone && errors?.telephone}
                              />
                            </div>
                          </Grid>
                          <Grid xs={12}>
                            <Typography className="mb-2 mt-4" variant="h6">
                              Street <span className="text-error">*</span>
                            </Typography>
                            <TextField
                              fullWidth
                              variant="outlined"
                              name="street"
                              value={values.street}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched?.street && Boolean(errors?.street)}
                              helperText={touched?.street && errors?.street}
                            />
                          </Grid>
                          <Grid xs={12} sm={6}>
                            <Typography className="mb-2 mt-4" variant="h6">
                              City <span className="text-error">*</span>
                            </Typography>
                            <TextField
                              id="outlined-number"
                              fullWidth
                              variant="outlined"
                              name="city"
                              value={values.city}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched?.city && Boolean(errors?.city)}
                              helperText={touched?.city && errors?.city}
                            />
                          </Grid>
                          <Grid xs={12} sm={6}>
                            <div className="ml-2">
                              <Typography className="mb-2 mt-4" variant="h6">
                                State/Province <span className="text-error">*</span>
                              </Typography>
                              <Select
                                fullWidth
                                variant="outlined"
                                name="state"
                                value={values.state}
                                onChange={(event) => {
                                  setFieldValue('state', event.target.value);
                                }}
                                onBlur={handleBlur}
                              >
                                {states.map((el) => (
                                  <MenuItem key={el.value} value={el.value}>
                                    {el.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </div>
                          </Grid>
                          <Grid xs={12} sm={6}>
                            <Typography className="mb-2 mt-4" variant="h6">
                              Zip Code <span className="text-error">*</span>
                            </Typography>
                            <TextField
                              fullWidth
                              variant="outlined"
                              name="postcode"
                              value={values.postcode}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched?.postcode && Boolean(errors?.postcode)}
                              helperText={touched?.postcode && errors?.postcode}
                            />
                          </Grid>
                          <Grid xs={12} sm={6}>
                            <div className="ml-2">
                              <Typography className="mb-2 mt-4" variant="h6">
                                Country <span className="text-error">*</span>
                              </Typography>
                              <Select
                                fullWidth
                                variant="outlined"
                                name="country_id"
                                value={values.country_id}
                                onChange={(event) => {
                                  setFieldValue('country_id', event.target.value);
                                }}
                                onBlur={handleBlur}
                              >
                                {Object.keys(countriesCodes).map((el) => (
                                  <MenuItem key={el} value={el}>
                                    {countriesCodes[el]}
                                  </MenuItem>
                                ))}
                              </Select>
                            </div>
                          </Grid>
                        </>
                        :
                        null}
                    </>
                    :
                    <>
                      {subscriptionStorePickupAddresses.map((el) => (
                        <Grid xs={12} key={el.name} className="ml-2 mt-5">
                          <Grid xs={12}>
                            <Typography variant="h6">
                              {el.name}
                            </Typography>
                          </Grid>
                          <Grid xs={12}>
                            <Typography>
                              {el.address_line1}
                            </Typography>
                          </Grid>
                          <Grid xs={12}>
                            <Typography>
                              {el.address_line2}
                            </Typography>
                          </Grid>
                          <Grid xs={12}>
                            <Typography>
                              {`${el.city}, ${states.find((state) => state.shortName === el.state).name} ${el.postal_code}`}
                            </Typography>
                          </Grid>
                          <Grid xs={12}>
                            <Typography>
                              {countriesCodes[el.country_code]}
                            </Typography>
                          </Grid>
                          {el.main_phone &&
                            <Grid xs={12}>
                              <div className="flex mt-2">
                                <Icon className="mr-2" fontSize="small">phone</Icon>
                                <div>
                                  <Typography>{el.main_phone}</Typography>
                                </div>
                              </div>
                            </Grid>
                          }
                          {el.email &&
                            <Grid xs={12}>
                              <div className="flex">
                                <Icon className="mr-2" fontSize="small">email</Icon>
                                <div>
                                  <Typography>{el.email}</Typography>
                                </div>
                              </div>
                            </Grid>
                          }
                          {el.hours &&
                            <Grid xs={12} className="mt-2">
                              {el.hours.map((hour) => (
                                <div key={hour.day}>
                                  {`${hour.day} ${hour.from} - ${hour.to}`}
                                </div>
                              ))}
                            </Grid>
                          }
                        </Grid>
                      ))}
                    </>
                  }
                </Grid>
              </Grid>
            </Grid>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              className="mt-4"
            >
              Save Subscription
            </Button>
            <Button
              variant="outlined"
              color="primary"
              className="mt-4 ml-2"
              onClick={() => history.push(goBackPath)}
            >
              Go Back
            </Button>
          </form>
        )}
      </Formik>
    </>
  );
};

export default ShippingForm;
